import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {changeClientMessagesFilters, searchClientMessages} from 'GenericTexting/ducks/clientMessage';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {withStringFilterProperties, withInequalityFilterProperties} from 'Shared/components/PagedView/Filter';
import './ClientMessages.sass';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {MenuItem} from '@material-ui/core';
import {sendMessagesByClient} from 'GenericTexting/ducks/message';
import {hasScope} from 'Shared/components/RequireScope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {Scope} from 'Shared/constants/Scope';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader from 'Shared/components/Toolbar/ToolbarHeader';
import PagedView from 'Shared/components/PagedView/PagedView';

const ClientMessages = () => {
  const dispatch = useDispatch();
  const scopes = useSelector(selectUserScopes);
  const userHasScope = hasScope(scopes);
  const {clientMessagePage, filter} = useSelector((state: ReducerState) => state.clientMessages);

  const changeFilter = withValueChangedHook(filter, changeClientMessagesFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };

  useEffect(() => {
    dispatch(searchClientMessages(undefined, filter));
  }, [dispatch, filter]);

  return (
    <ModularHost>
      <ToolbarHeader title="Client Messages" />
      <PagedView
        pagedViewKey="clientMessages"
        data={clientMessagePage}
        loadMore={page => dispatch(searchClientMessages(page, filter))}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Client Id"
              filterProperties={withStringFilterProperties(filterProperties, 'clientId')}
            >
              {clientMessage => clientMessage.client!.id!}
            </Column>
            <Column
              {...columnProps}
              title="Client Name"
              filterProperties={withStringFilterProperties(filterProperties, 'clientName')}
              default
            >
              {clientMessage => clientMessage.client!.clientName}
            </Column>
            <Column
              {...columnProps}
              title="Message Count"
              filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfMessages')}
              default
            >
              {clientMessage => clientMessage.numberOfMessages}
            </Column>
            <Column
              {...columnProps}
              title="Opt-In Count"
              filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfOptInMessages')}
              default
            >
              {clientMessage => clientMessage.numberOfOptInMessages}
            </Column>
            <Column
              {...columnProps}
              title="Phone Number Count"
              filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfPhoneNumbers')}
              default
            >
              {clientMessage => clientMessage.numberOfPhoneNumbers}
            </Column>
            <Column
              {...columnProps}
              title="Oldest Message (In Days)"
              filterProperties={withInequalityFilterProperties(filterProperties, 'ageOfOldestMessageInDays')}
              default
            >
              {clientMessage => clientMessage.ageOfOldestMessageInDays}
            </Column>
            <Column
              {...columnProps}
              title="Newest Message (In Days)"
              filterProperties={withInequalityFilterProperties(filterProperties, 'ageOfNewestMessageInDays')}
              default
            >
              {clientMessage => clientMessage.ageOfNewestMessageInDays}
            </Column>
            {userHasScope(Scope.SendMessages) && (
              <Column title="Actions" {...columnProps} isActions default>
                {messages => (
                  <>
                    <ActionsMenu>
                      <MenuItem
                        onClick={() => {
                          dispatch(sendMessagesByClient(messages.client!));
                        }}
                      >
                        Send
                      </MenuItem>
                    </ActionsMenu>
                  </>
                )}
              </Column>
            )}
          </>
        )}
      </PagedView>
    </ModularHost>
  );
};

export default ClientMessages;
